import { addMonths, isAfter, subMonths, subWeeks } from "date-fns"
import { useFlags } from "~/features/feature-flags"
import { useMemo } from "react"

import { useVariantsByIds } from "~/hooks/useVariants"
import { type SalesAndForecast } from "~/models/variant"
import { api } from "~/utils/api"
import {
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  differenceInCalendarDaysUTC,
  getStartOfCurrentWeekUTC,
  startOfTodayUTC,
} from "~/utils/datetime"
import { financeTableColors, roundForecast } from "~/utils/formatting"
import {
  addMissingDatesSalesAndForecast,
  convertWeeklyToFullMonthly,
  isFutureForecast,
} from "~/utils/weeklyToMonthly"
import { snapRangeToFiscalPeriods } from "~/utils/fiscalCalendar"

import { useChartSeriesConfig } from "~/constants/colors"
import { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { formatName } from "~/utils/namingUtils"
import { isPreorderVisible } from "~/utils/unfulfillablePreorderUtils"
import { useProductForecast } from "~/hooks/useProductForecast"
import { sortBy, sum } from "lodash"

type Props = {
  variantIds: string[]
  warehouseId: string | undefined
  monthly: boolean
  startDate: Date
  endDate: Date
  channel: string | undefined
  skuId?: string
}

export function useForecastGraph({
  variantIds,
  warehouseId,
  monthly,
  startDate,
  endDate,
  channel,
  skuId,
}: Props) {
  const variants = useVariantsByIds(variantIds)

  // Fiscal companies bucket the monthly view by fiscal periods; fetch whole periods so the
  // edge buckets have all their weeks and aren't dropped as incomplete.
  const { start: queryStartDate, end: queryEndDate } = useMemo(
    () =>
      monthly ? snapRangeToFiscalPeriods(startDate, endDate) : { start: startDate, end: endDate },
    [monthly, startDate, endDate],
  )

  const { data: salesAndForecast, isLoading } = api.product.salesAndForecasts.useQuery({
    warehouseId,
    variantIds,
    skuIds: skuId ? [skuId] : undefined,
    startDate: queryStartDate,
    endDate: queryEndDate,
    channel,
  })

  const { forecasts: productForecasts } = useProductForecast(
    variantIds,
    warehouseId,
    channel,
    queryStartDate,
    queryEndDate,
    skuId,
  )

  const { unfulfillablePreorders: unfulfillablePreordersFlag } = useFlags()

  const chartConfig = useChartSeriesConfig()

  return useMemo(() => {
    if (variants.length <= 0 || !salesAndForecast || !productForecasts || isLoading) {
      return {
        isLoading,
        salesGraphData: {} as DateChartSeries,
        adjustedForecastGraphData: {} as DateChartSeries,
        rawForecastGraphData: {} as DateChartSeries,
        adjustedAndForecastIsSame: false,
        hasDemandPlanForecast: false,
        knownPreOrdersGraphData: {} as DateChartSeries,
        invalidPreOrdersGraphData: {} as DateChartSeries,
        unfulfillablePreOrdersGraphData: {} as DateChartSeries,
        hasUnfulfillablePreOrders: false,
        atRiskPreOrdersGraphData: {} as DateChartSeries,
        hasAtRiskPreOrders: false,
        hasInvalidPreOrders: false,
        invalidVariantCount: 0,

        itemProfitGraphData: {} as DateChartSeries,
        profitGraphData: {} as DateChartSeries,
        futureProfitGraphData: {} as DateChartSeries,
        futureItemProfitGraph: {} as DateChartSeries,
        itemPriceGraphData: {} as DateChartSeries,
        futureItemPriceGraphData: {} as DateChartSeries,
        revenueGraphData: {} as DateChartSeries,
        futureRevenueGraphData: {} as DateChartSeries,
        GrossMarginGraphData: {} as DateChartSeries,
        futureGrossMarginGraphData: {} as DateChartSeries,
        discountPercentageGraphData: {} as DateChartSeries,
        futureDiscountPercentageGraphData: {} as DateChartSeries,
      }
    }

    // SALES & Forecast
    const sales: SalesAndForecast[] =
      salesAndForecast?.sales.map(({ date, value: quantity, revenue, discountQuantity }) => ({
        date,
        future: false,
        quantity,
        revenue,
        discountQuantity,
      })) ?? []

    const forecasts: SalesAndForecast[] =
      productForecasts?.map(({ date, value: quantity }) => ({
        date,
        future: true,
        quantity,
      })) ?? []

    // For SKU-level graphs there is no separate raw vs adjusted forecast —
    // use productForecasts (already SKU-level) for both to avoid showing
    // variant-level raw forecast against SKU-level adjusted forecast.
    const rawForecasts: SalesAndForecast[] = skuId
      ? forecasts
      : (salesAndForecast?.rawForecasts.map(({ date, value }) => ({
          date,
          future: true,
          quantity: value,
        })) ?? [])

    // Net sales = sales - returns (returns are positive quantities)
    const netSales: SalesAndForecast[] =
      salesAndForecast?.sales.map(({ date, value, revenue, discountQuantity, returns }) => ({
        date,
        future: false,
        quantity: (value ?? 0) - (returns ?? 0),
        revenue,
        discountQuantity,
      })) ?? []

    const salesAndForecastData = sortBy([...sales, ...forecasts], (data) => data.date)
    const salesAndRawForecastData = sortBy([...sales, ...rawForecasts], (data) => data.date)
    const netSalesAndForecastData = sortBy([...netSales, ...forecasts], (data) => data.date)

    const firstSalesDate = salesAndForecastData[0] ? salesAndForecastData[0].date : null
    const firstSalesDateOrOneMonthAgo =
      !firstSalesDate || differenceInCalendarDaysUTC(startOfTodayUTC(), firstSalesDate) < 31
        ? subMonths(startOfTodayUTC(), 1)
        : firstSalesDate

    const lastForecastDate =
      salesAndForecastData.length > 0 && salesAndForecastData[salesAndForecastData.length - 1]
        ? salesAndForecastData[salesAndForecastData.length - 1]!.date
        : addMonths(startOfTodayUTC(), 1)

    // Fill whole fiscal periods in monthly view so edge buckets aren't dropped as incomplete.
    const { start: fillStart, end: fillEnd } = monthly
      ? snapRangeToFiscalPeriods(firstSalesDateOrOneMonthAgo, lastForecastDate)
      : { start: firstSalesDateOrOneMonthAgo, end: lastForecastDate }

    const salesAndRawForecastWithMissing = addMissingDatesSalesAndForecast(
      salesAndRawForecastData,
      fillStart,
      fillEnd,
    )
    const finalRawForecastData = monthly
      ? convertWeeklyToFullMonthly(salesAndRawForecastWithMissing)
      : salesAndRawForecastWithMissing

    const salesAndForecastWithMissing = addMissingDatesSalesAndForecast(
      salesAndForecastData,
      fillStart,
      fillEnd,
    )
    const finalSalesAndForecastData = monthly
      ? convertWeeklyToFullMonthly(salesAndForecastWithMissing)
      : salesAndForecastWithMissing

    const netSalesAndForecastWithMissing = addMissingDatesSalesAndForecast(
      netSalesAndForecastData,
      fillStart,
      fillEnd,
    )
    const finalNetSalesAndForecastData = monthly
      ? convertWeeklyToFullMonthly(netSalesAndForecastWithMissing)
      : netSalesAndForecastWithMissing

    const knownPreOrders = salesAndForecast["preOrders"].map(({ date, value }) => ({
      date,
      quantity: value,
      future: false,
    }))

    const finalKnownPreOrders = monthly
      ? convertWeeklyToFullMonthly(knownPreOrders, queryStartDate, queryEndDate)
      : knownPreOrders

    const invalidPreOrders = (salesAndForecast["invalidPreOrders"] ?? []).map(
      ({ date, value }) => ({
        date,
        quantity: value,
        future: false,
      }),
    )

    const finalInvalidPreOrders = monthly
      ? convertWeeklyToFullMonthly(invalidPreOrders, queryStartDate, queryEndDate)
      : invalidPreOrders

    // Precompute future split indexes and create reusable slices to avoid repeated .findIndex() and .filter() calls
    const salesFutureIdx = finalSalesAndForecastData.findIndex((d) =>
      isFutureForecast(d.date, monthly),
    )
    const salesHistorical = finalSalesAndForecastData.slice(0, salesFutureIdx)
    const salesFuture = finalSalesAndForecastData.slice(salesFutureIdx)

    const rawFutureIdx = finalRawForecastData.findIndex((d) => isFutureForecast(d.date, monthly))
    const rawFuture = finalRawForecastData.slice(rawFutureIdx)

    const netFutureIdx = finalNetSalesAndForecastData.findIndex((d) =>
      isFutureForecast(d.date, monthly),
    )
    const netHistorical = finalNetSalesAndForecastData.slice(0, netFutureIdx)

    const knownPreOrdersMap = new Map(
      finalKnownPreOrders.map((po) => [po.date.getTime(), po.quantity]),
    )
    const unknownPreOrdersMap = new Map(
      (salesAndForecast["unknownPreOrders"] ?? []).map(({ date, rawPreorderForecast }) => [
        date.getTime(),
        rawPreorderForecast,
      ]),
    )

    const startLastThreeMonths = subWeeks(getStartOfCurrentWeekUTC(), 12)
    // Use original sales data (not monthly-converted) to calculate avgItemPriceValue
    // This ensures we have data even when monthly view filters out partial historical months
    const itemPrices = sales
      .filter((data) => isAfter(data.date, startLastThreeMonths))
      .map((data) => (data.revenue && data.quantity ? data.revenue / data.quantity : 0))
    const avgItemPriceValue = itemPrices.length > 0 ? sum(itemPrices) / itemPrices.length : 0

    const costPrice = variants.find((v) => v.costPrice)?.costPrice

    const revenueGraphData: DateChartSeries = {
      name: "Revenue",
      data: salesHistorical.map((sale) => ({
        milliseconds: sale.date.getTime(),
        value: sale.revenue ?? 0,
      })),
      type: "spline",
      lineWidth: 3,
      colors: [financeTableColors.revenue],
      dashStyle: "Solid",
    }

    const futureRevenueGraphData: DateChartSeries = {
      name: "Future Revenue",
      data: rawFuture.map((forecast) => ({
        milliseconds: forecast.date.getTime(),
        value: forecast.quantity ? roundForecast(forecast.quantity) * avgItemPriceValue : 0,
      })),
      type: "spline",
      colors: [financeTableColors.revenue],
      dashStyle: "Dash",
      custom: {
        legendTooltip: `Based on the average sales price of the last 3 months of ${formatName("sale", true)}`,
      },
    }

    const profitGraphData: DateChartSeries = {
      name: "Profit",
      data: salesHistorical.map((sale) => ({
        milliseconds: sale.date.getTime(),
        value:
          sale.revenue && sale.quantity && costPrice ? sale.revenue - sale.quantity * costPrice : 0,
      })),
      type: "spline",
      lineWidth: 3,
      colors: [financeTableColors.profit],
      dashStyle: "Solid",
    }

    const futureProfitGraphData: DateChartSeries = {
      name: "Future Profit",
      data: rawFuture.map((forecast) => ({
        milliseconds: forecast.date.getTime(),
        value:
          forecast.quantity && costPrice
            ? forecast.quantity * avgItemPriceValue - forecast.quantity * costPrice
            : 0,
      })),
      type: "spline",
      colors: [financeTableColors.profit],
      dashStyle: "Dash",
      custom: {
        legendTooltip: `Based on the average sales price of the last 3 months of ${formatName("sale", true)} and the latest cost price`,
      },
    }

    const itemPriceGraphData: DateChartSeries = {
      name: `Avg. ${formatName("sale", true, true)} Price`,
      data: salesHistorical
        .filter((data) => data.quantity && data.quantity > 0 && data.revenue !== null)
        .map((sale) => ({
          milliseconds: sale.date.getTime(),
          value: Math.abs(sale.revenue! / Math.abs(sale.quantity!)),
        })),
      type: "spline",
      lineWidth: 3,
      colors: [financeTableColors.avgItemPrice],
      dashStyle: "Solid",
      custom: {
        legendTooltip: "The average sales price per item sold",
      },
    }

    const futureItemPriceGraphData: DateChartSeries = {
      name: `Future Avg. ${formatName("sale", true, true)} Price`,
      data: rawFuture.map((forecast) => ({
        milliseconds: forecast.date.getTime(),
        value: avgItemPriceValue,
      })),
      type: "spline",
      colors: [financeTableColors.avgItemPrice],
      dashStyle: "Dash",
      custom: {
        legendTooltip: `Based on the average item price of the last 3 months of ${formatName("sale", true)}`,
      },
    }

    const itemProfitGraphData: DateChartSeries = {
      name: "Avg. Profit",
      data: salesHistorical
        .filter((data) => data.quantity && data.quantity > 0 && data.revenue !== null)
        .map((sale) => ({
          milliseconds: sale.date.getTime(),
          value:
            sale.revenue && sale.quantity && costPrice
              ? sale.revenue / sale.quantity - costPrice
              : 0,
        })),
      type: "spline",
      lineWidth: 3,
      colors: [chartConfig.itemProfit.color],
      dashStyle: chartConfig.itemProfit.dashStyle,
      custom: {
        legendTooltip: "The average profit per item sold",
      },
    }

    const futureItemProfitGraph: DateChartSeries = {
      name: "Future Avg. Profit",
      data: rawFuture.map((forecast) => ({
        milliseconds: forecast.date.getTime(),
        value: costPrice ? avgItemPriceValue - costPrice : 0,
      })),
      type: "spline",
      colors: [chartConfig.futureItemProfit.color],
      dashStyle: chartConfig.futureItemProfit.dashStyle,
      custom: {
        legendTooltip: `The average profit per item sold in the future based on the average sales price of the last 3 months of ${formatName("sale", true)} and the latest cost price`,
      },
    }

    const GrossMarginGraphData: DateChartSeries = {
      name: "Gross Margin",
      data: salesHistorical.map((sale) => ({
        milliseconds: sale.date.getTime(),
        value:
          sale.revenue && sale.quantity && costPrice
            ? ((sale.revenue - sale.quantity * costPrice) / sale.revenue) * 100
            : 0,
      })),
      type: "spline",
      colors: [financeTableColors.profit],
      dashStyle: "Solid",
    }

    const futureGrossMarginGraphData: DateChartSeries = {
      name: "Future Gross Margin",
      data: rawFuture.map((forecast) => ({
        milliseconds: forecast.date.getTime(),
        value:
          forecast.quantity && costPrice
            ? ((forecast.quantity * avgItemPriceValue - forecast.quantity * costPrice) /
                (forecast.quantity * avgItemPriceValue)) *
              100
            : 0,
      })),
      type: "spline",
      colors: [financeTableColors.profit],
      dashStyle: "Dash",
      custom: {
        legendTooltip: `Based on the average sales price of the last 3 months of ${formatName("sale", true)} and the latest cost price`,
      },
    }

    // Compute avgDiscountPercentage once (used in 2 places below)
    // Use original sales data (not monthly-converted) for same reason as avgItemPriceValue
    const discountPercentages = sales
      .filter((data) => isAfter(data.date, startLastThreeMonths))
      .map((data) => {
        if (
          data.quantity &&
          data.quantity > 0 &&
          data.discountQuantity !== null &&
          data.discountQuantity !== undefined
        ) {
          return data.discountQuantity / data.quantity
        }
        return 0
      })
      .filter((percentage) => percentage > 0)
    const avgDiscountPercentageValue =
      discountPercentages.length > 0 ? sum(discountPercentages) / discountPercentages.length : 0

    const discountPercentageGraphData: DateChartSeries = {
      name: "Discount Percentage",
      data: salesHistorical.map((sale) => ({
        milliseconds: sale.date.getTime(),
        value:
          sale.quantity &&
          sale.quantity > 0 &&
          sale.discountQuantity !== null &&
          sale.discountQuantity !== undefined
            ? sale.discountQuantity / sale.quantity
            : 0,
      })),
      type: "spline",
      lineWidth: 3,
      colors: [financeTableColors.discount],
      dashStyle: "Solid",
      custom: {
        legendTooltip: "The avg. discount percentage of the items sold.",
      },
    }

    const futureDiscountPercentageGraphData: DateChartSeries = {
      name: "Future Discount Percentage",
      data: rawFuture.map((forecast) => ({
        milliseconds: forecast.date.getTime(),
        value: avgDiscountPercentageValue,
      })),
      type: "spline",
      colors: [financeTableColors.discount],
      dashStyle: "Dash",
      custom: {
        legendTooltip: `Based on the average discount percentage of the last 3 months of ${formatName("sale", true)}`,
      },
    }

    const salesGraphData: DateChartSeries = {
      name: formatName("sale", true, true),
      data: salesHistorical.map((sale) => ({
        milliseconds: sale.date.getTime(),
        value: Math.round(sale.quantity ?? 0),
      })),
      type: "areaspline",
      lineWidth: 3,
      colors: [chartConfig.sales.color],
      dashStyle: chartConfig.sales.dashStyle,
    }

    const netSalesGraphData: DateChartSeries = {
      name: `Net ${formatName("sale", true, true)}`,
      data: netHistorical.map((sale) => ({
        milliseconds: sale.date.getTime(),
        value: Math.round(sale.quantity ?? 0),
      })),
      type: "areaspline",
      lineWidth: 3,
      colors: [chartConfig.netSales.color],
      dashStyle: chartConfig.netSales.dashStyle,
    }

    const hasDemandPlanForecast = salesAndForecast.hasDemandPlanForecast
    const hasNoosVariants = variants.some((v) => v.productType === "noos")
    const hasFashionVariants = variants.some((v) => v.productType === "fashion")
    const isMixedNoosAndFashion = hasNoosVariants && hasFashionVariants

    const adjustedData = salesFuture.map((forecast) => ({
      milliseconds: forecast.date.getTime(),
      value: forecast.quantity && roundForecast(forecast.quantity),
    }))
    const rawData = rawFuture.map((forecast) => ({
      milliseconds: forecast.date.getTime(),
      value: forecast.quantity && roundForecast(forecast.quantity),
    }))

    const adjustedAndForecastIsSame =
      adjustedData.length === rawData.length &&
      adjustedData.every(
        (d, i) => d.milliseconds === rawData[i]?.milliseconds && d.value === rawData[i]?.value,
      )

    // Demand starts at the current bucket while sales ends at the last completed one; seeding
    // demand with the sales line's own final point closes the gap at today. Reusing that point
    // rather than re-deriving it keeps the join exact, since the two round differently.
    const lastSalesPoint = salesGraphData.data[salesGraphData.data.length - 1]
    const bridgedAdjustedData =
      lastSalesPoint && adjustedData.length > 0 ? [lastSalesPoint, ...adjustedData] : adjustedData

    const adjustedForecastGraphData: DateChartSeries = {
      name: hasDemandPlanForecast
        ? isMixedNoosAndFashion
          ? "Demand"
          : adjustedAndForecastIsSame
            ? "Demand Plan"
            : "Demand Plan + Adjustments"
        : "Demand",
      data: bridgedAdjustedData,
      type: "areaspline",
      colors: [chartConfig.demand.color],
      lineWidth: 3,
      dashStyle: chartConfig.demand.dashStyle,
    }

    const rawForecastGraphData: DateChartSeries = {
      name: "Raw Demand",
      data: rawData,
      type: "spline",
      colors: [chartConfig.rawDemand.color],
      dashStyle: chartConfig.rawDemand.dashStyle,
    }

    const netSalesAndForecastGraphData: DateChartSeries = {
      name: `Net ${formatName("sale", true, true)} / Demand`,
      data: finalNetSalesAndForecastData.map((item) => ({
        milliseconds: item.date.getTime(),
        value: !isFutureForecast(item.date, monthly)
          ? Math.round(item.quantity ?? 0)
          : (item.quantity ?? 0),
      })),
      type: "spline",
      lineWidth: 2,
      colors: [chartConfig.netSales.color],
      dashStyle: chartConfig.netSales.dashStyle,
    }

    const knownPreOrdersGraphData: DateChartSeries = {
      // eslint-disable-next-line @hakio/no-untranslated-terminology -- internal series id; user-facing label is `displayName`
      name: `Known Preorders`,
      displayName: `Known ${formatName("preorder", true, true)}`,
      data: finalKnownPreOrders.map((preOrder) => ({
        milliseconds: preOrder.date.getTime(),
        value: preOrder.quantity,
      })),
      type: "column",
      colors: [chartConfig.futurePreOrders.color],
      dashStyle: chartConfig.futurePreOrders.dashStyle,
    }

    const invalidPreOrdersGraphData: DateChartSeries = {
      // eslint-disable-next-line @hakio/no-untranslated-terminology -- internal series id; user-facing label is `displayName`
      name: `Invalid Preorders`,
      displayName: `Invalid ${formatName("preorder", true, true)}`,
      data: finalInvalidPreOrders.map((preOrder) => ({
        milliseconds: preOrder.date.getTime(),
        value: preOrder.quantity,
      })),
      type: "column",
      colors: [chartConfig.invalidPreOrder.color],
      dashStyle: chartConfig.invalidPreOrder.dashStyle,
    }

    const hasInvalidPreOrders = finalInvalidPreOrders.some((po) => (po.quantity ?? 0) > 0)
    const invalidVariantCount = salesAndForecast["invalidVariantCount"] ?? 0

    const redBuckets = new Map<number, number>()
    const yellowBuckets = new Map<number, number>()
    for (const v of variants) {
      for (const po of v.futureKnownPreorders ?? []) {
        if (!isPreorderVisible(po, warehouseId, channel)) continue
        const bucket = (
          monthly
            ? convertToStartOfMonthUTC(po.deliveryDate)
            : convertToStartOfWeekUTC(po.deliveryDate)
        ).getTime()
        if (po.unfulfillableQty > 0)
          redBuckets.set(bucket, (redBuckets.get(bucket) ?? 0) + po.unfulfillableQty)
        if (po.atRiskQty > 0)
          yellowBuckets.set(bucket, (yellowBuckets.get(bucket) ?? 0) + po.atRiskQty)
      }
    }
    // Visual stacking trick: yellow series renders at (unfulfillable + at-risk) total height so
    // it forms the background; red unfulfillable series is drawn on top covering the bottom
    // portion — net result is red on bottom, yellow above. grouping: false lets both overlay.
    // tooltipValue carries the actual at-risk qty so the tooltip shows the right number.
    const allBuckets = new Set<number>([...redBuckets.keys(), ...yellowBuckets.keys()])
    const unfulfillablePreOrdersGraphData: DateChartSeries = {
      // eslint-disable-next-line @hakio/no-untranslated-terminology
      name: `Unfulfillable Preorders`,
      displayName: formatName("unfulfillable_preorder", true, true),
      data: [...redBuckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([ms, qty]) => ({ milliseconds: ms, value: qty })),
      type: "column",
      colors: [chartConfig.unfulfillablePreOrder.color],
      dashStyle: chartConfig.unfulfillablePreOrder.dashStyle,
    }
    const atRiskPreOrdersGraphData: DateChartSeries = {
      // eslint-disable-next-line @hakio/no-untranslated-terminology
      name: `At-risk Preorders`,
      displayName: formatName("at_risk_preorder", true, true),
      data: [...allBuckets]
        .sort((a, b) => a - b)
        .map((ms) => {
          const red = redBuckets.get(ms) ?? 0
          const yellow = yellowBuckets.get(ms) ?? 0
          return { milliseconds: ms, value: red + yellow, tooltipValue: yellow }
        }),
      type: "column",
      colors: [chartConfig.atRiskPreOrder.color],
      dashStyle: chartConfig.atRiskPreOrder.dashStyle,
    }
    const hasUnfulfillablePreOrders = unfulfillablePreordersFlag && redBuckets.size > 0
    const hasAtRiskPreOrders = unfulfillablePreordersFlag && yellowBuckets.size > 0

    const forecastWithoutPreOrdersGraphData: DateChartSeries = {
      name: `Forecasted reorders`,
      data: (hasDemandPlanForecast ? salesFuture : rawFuture).map((forecast) => {
        const preOrderValue = knownPreOrdersMap.get(forecast.date.getTime()) ?? 0
        const unknownPreOrderValue = unknownPreOrdersMap.get(forecast.date.getTime()) ?? 0
        return {
          milliseconds: forecast.date.getTime(),
          value: roundForecast((forecast.quantity ?? 0) - preOrderValue - unknownPreOrderValue),
        }
      }),
      type: "spline",
      colors: [chartConfig.forecastWithoutPreOrders.color],
      dashStyle: chartConfig.forecastWithoutPreOrders.dashStyle,
      custom: {
        legendTooltip: `Expected demand excluding known and forecasted ${formatName("preorder", true)}`,
      },
    }

    return {
      isLoading,
      itemProfitGraphData,
      profitGraphData,
      futureProfitGraphData,
      futureItemProfitGraph,
      itemPriceGraphData,
      futureItemPriceGraphData,
      revenueGraphData,
      futureRevenueGraphData,
      salesGraphData,
      netSalesGraphData,
      adjustedForecastGraphData,
      rawForecastGraphData,
      netSalesAndForecastGraphData,
      knownPreOrdersGraphData,
      invalidPreOrdersGraphData,
      unfulfillablePreOrdersGraphData,
      hasUnfulfillablePreOrders,
      atRiskPreOrdersGraphData,
      hasAtRiskPreOrders,
      hasInvalidPreOrders,
      invalidVariantCount,
      forecastWithoutPreOrdersGraphData,
      adjustedAndForecastIsSame,
      hasDemandPlanForecast,
      GrossMarginGraphData,
      futureGrossMarginGraphData,
      discountPercentageGraphData,
      futureDiscountPercentageGraphData,
    }
  }, [
    queryStartDate,
    queryEndDate,
    variantIds,
    monthly,
    salesAndForecast,
    productForecasts,
    unfulfillablePreordersFlag,
    variants,
    isLoading,
    skuId,
    chartConfig,
  ])
}
